import React from "react"
import PropTypes from "prop-types"

import useAvailabilityForm, { agruparCamiones } from "../hooks/useAvailabilityForm"
import { etiquetaRegion } from "../helpers"
import AvailabilityForm from "./AvailabilityForm"
import { StarsReadOnly } from "./StarRating"

const AvailabilityCard = ({
  daily,
  regions,
  farm,
  product,
  farmId,
  isExpanded,
  isEditing,
  isSaving,
  canEdit = true,
  onToggle,
  onStartEdit,
  onCancelEdit,
  onUpdate,
  onSaveZones,
}) => {
  const form = useAvailabilityForm(daily, regions)

  const onGuardar = async () => {
    const mensaje = form.validar()
    if (mensaje) {
      form.setError(mensaje)
      return
    }
    await onUpdate(form.construirRegistro({ farmId, product }))
  }

  const onCancelar = () => {
    form.events.reset()
    onCancelEdit()
  }

  const camiones = daily.quantities || []
  // se muestran agrupados igual que se capturan: N camiones de X unidades
  const grupos = camiones.length ? agruparCamiones(camiones) : []

  return (
    <div className={`av-entry${isEditing ? " is-editing" : ""}`}>
      <div
        className="av-entry-head"
        onClick={isEditing ? undefined : onToggle}
        role="button"
        tabIndex={0}
        onKeyPress={event => {
          if (!isEditing && event.key === "Enter") onToggle()
        }}
      >
        <div className="av-entry-chip">{daily.product}</div>
        <span className="av-entry-weight av-num">
          {daily.weight} – {daily.weight_max} kg
        </span>
        <div className="d-flex align-items-center gap-2">
          <StarsReadOnly value={Number(daily.quality) || 0} />
          <StarsReadOnly value={Number(daily.quality_2) || 0} />
          <StarsReadOnly value={Number(daily.quality_3) || 0} />
        </div>
        <div className="av-entry-meta">
          <i className="bx bx-bus" />
          {camiones.length} {camiones.length === 1 ? "camión" : "camiones"}
        </div>
        <div className="av-entry-meta av-num">{daily.quantity} pzas</div>

        <div className="av-entry-actions">
          {canEdit && (
            <button
              type="button"
              className="av-btn-icon"
              title="Editar disponibilidad"
              onClick={event => {
                event.stopPropagation()
                onStartEdit()
              }}
            >
              <i className="bx bx-edit-alt" />
            </button>
          )}
          <span
            className={`av-chevron${isExpanded || isEditing ? " is-open" : ""}`}
          >
            <i className="bx bx-chevron-down" />
          </span>
        </div>
      </div>

      {isExpanded && !isEditing && (
        <div className="av-entry-view">
          <div>
            <div className="av-view-title">Camiones</div>
            {camiones.length === 0 && (
              <div className="av-view-obs">Sin camiones registrados.</div>
            )}
            {grupos.map(grupo => (
              <div className="av-view-row" key={grupo.key}>
                <b>
                  {grupo.count}{" "}
                  {grupo.count === "1" ? "camión" : "camiones"}
                </b>
                <span className="av-num">{grupo.units} uds./camión</span>
              </div>
            ))}
            {daily.observations && (
              <>
                <div className="av-view-title mt-3">Observaciones</div>
                <div className="av-view-obs">{daily.observations}</div>
              </>
            )}
          </div>

          <div>
            <div className="av-view-title">Precios</div>
            {(daily.prices || []).length === 0 && (
              <div className="av-view-obs">Sin precios registrados.</div>
            )}
            {(daily.prices || []).map(price => (
              <React.Fragment key={price.id}>
                {parseFloat(price.price_shipped) > 0 && (
                  <div className="av-view-row">
                    <span>{nombreZona(price, regions)} — Destino</span>
                    <span className="av-num">${price.price_shipped}</span>
                  </div>
                )}
                {parseFloat(price.price_local) > 0 && (
                  <div className="av-view-row">
                    <span>{nombreZona(price, regions)} — Granja</span>
                    <span className="av-num">${price.price_local}</span>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {isEditing && (
        <div className="av-entry-edit">
          <AvailabilityForm
            form={form}
            regions={regions}
            farm={farm}
            onSaveZones={onSaveZones}
          />
          <div className="av-edit-actions">
            <button
              type="button"
              className="av-btn-ghost"
              disabled={isSaving}
              onClick={onCancelar}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={`av-btn-save${isSaving ? " is-saving" : ""}`}
              disabled={isSaving}
              onClick={onGuardar}
            >
              {isSaving ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function nombreZona(price, regions) {
  if (price.subregion) return `${price.region} | ${price.subregion}`
  if (price.region) return price.region
  const region = (regions || []).find(
    item => item.id === (price.subregion_id || price.region_id)
  )
  return region ? etiquetaRegion(region, regions) : "Zona"
}

AvailabilityCard.propTypes = {
  daily: PropTypes.object.isRequired,
  regions: PropTypes.array.isRequired,
  farm: PropTypes.object,
  product: PropTypes.object.isRequired,
  farmId: PropTypes.number,
  isExpanded: PropTypes.bool,
  isEditing: PropTypes.bool,
  isSaving: PropTypes.bool,
  // fuera de la ventana de captura la tarjeta se consulta pero no se edita
  canEdit: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onStartEdit: PropTypes.func,
  onCancelEdit: PropTypes.func,
  onUpdate: PropTypes.func,
  onSaveZones: PropTypes.func,
}

export default AvailabilityCard
